import { useState, useEffect, useCallback, useRef, useMemo, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, X, Archive, FileText, Folder } from 'lucide-react';
import { SortableTree } from 'dnd-kit-sortable-tree';
import { saveDocDirectory, saveDocPage, deleteDocDirectory, deleteDocPage, archiveDocDirectory, archiveDocPage, unarchiveEntity, getDirectoryUserPermissions } from '../../../services/api';
import ConfirmTrashModal from '../../shared/ui/ConfirmTrashModal';
import DocMovePreviewModal from './DocMovePreviewModal';
import { useAuth } from '../../../context/AuthContext';
import useDocTreeDnd, { sortableId, buildTreeItems } from './useDocTreeDnd';
import { SHOW_ARCHIVED_KEY, readShowArchived, fetchJson, getApiBase, getPagesBase, transformDirectories, transformPages, mergeAndSortByWeight } from './docTreeApi';
import { TreeItemRow, DndTreeCtx, DndTreeItemComponent } from './DocTreeRow';
import ContextMenu from './DocTreeContextMenu';
import { createPortal } from 'react-dom';
import { DirectorySettingsBody } from '../settings/DocDirectorySettings';
import { BuilderCreateModal } from '../../builder/BuilderCreateModal';
import { BuilderTemplatePicker } from '../../builder/BuilderTemplatePicker';
import { SpaceFormBody } from '../settings/DocSpaceForm';
import { DocFiltersContext } from '../DocFiltersContext';
import { Picto } from '../../shared/ui/PictoIcon';

/* ════════════════════════════════════════════════════════════════
   DocSidebarTree — tree navigation body for space/page sidebar
   ════════════════════════════════════════════════════════════════ */

export default function DocSidebarTree({ searchRef, currentSpaceId = null }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const filtersCtx = useContext(DocFiltersContext);
    const localSearchRef = useRef(null);
    const effectiveSearchRef = searchRef || localSearchRef;

    const [showArchived, setShowArchived] = useState(readShowArchived);
    const showArchivedRef = useRef(showArchived);
    useEffect(() => { showArchivedRef.current = showArchived; }, [showArchived]);

    const [spaces, setSpaces] = useState([]);
    const [allDirs, setAllDirs] = useState([]);
    const childrenCacheRef = useRef(new Map());
    const [childrenVersion, setChildrenVersion] = useState(0);
    const [expandedDirs, setExpandedDirs] = useState(new Set());
    const [loadingDirs, setLoadingDirs] = useState(new Set());
    const [loading, setLoading] = useState(true);

    const [ctxMenu, setCtxMenu] = useState(null);
    const [settingsItem, setSettingsItem] = useState(null);
    const [renamingId, setRenamingId] = useState(null);

    // Création d'une page DANS un répertoire de l'arbre : `{ id, mode }`, où le
    // mode dit lequel des deux écrans du builder monte — les mêmes que la barre
    // d'un espace, pour que le geste soit le même partout.
    const [createFor, setCreateFor] = useState(null);
    const [createDirParent, setCreateDirParent] = useState(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [searchFocused, setSearchFocused] = useState(false);

    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [pendingArchive, setPendingArchive] = useState(null);
    const [archivingDoc, setArchivingDoc] = useState(false);
    const [pendingUnarchive, setPendingUnarchive] = useState(null);
    const [unarchivingDoc, setUnarchivingDoc] = useState(false);

    const isSpaceMode = currentSpaceId != null;

    /* ── DnD permissions (ABAC) ── */
    const [canDrag, setCanDrag] = useState(false);
    useEffect(() => {
        if (!isSpaceMode) { setCanDrag(false); return; }
        let cancelled = false;
        getDirectoryUserPermissions(currentSpaceId)
            .then((data) => { if (!cancelled && data) setCanDrag(!!data.isAdmin || !!data.isOwner); })
            .catch(() => { if (!cancelled) setCanDrag(false); });
        return () => { cancelled = true; };
    }, [isSpaceMode, currentSpaceId]);

    /* ── Pending move (cross-parent) ── */
    const [pendingMove, setPendingMove] = useState(null);

    /* ── DnD hook ── */
    const {
        handleItemsChanged,
    } = useDocTreeDnd(childrenCacheRef, setChildrenVersion, canDrag, currentSpaceId, setPendingMove);

    /* ── Initial load (re-triggers when showArchived or currentSpaceId changes) ── */
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        childrenCacheRef.current = new Map();
        setChildrenVersion(0);
        setExpandedDirs(new Set());

        if (isSpaceMode) {
            const safeId = encodeURIComponent(currentSpaceId);
            Promise.all([
                fetchJson(`${getApiBase(showArchived)}&filter[0][f]=parent&filter[0][v]=${safeId}&sort[0][f]=weight&sort[0][d]=ASC`),
                fetchJson(`${getPagesBase(showArchived)}&filter[0][f]=field_directory&filter[0][v]=${safeId}&sort[0][f]=field_weight&sort[0][d]=ASC`),
            ])
                .then(([dirs, pages]) => {
                    if (cancelled) return;
                    const children = mergeAndSortByWeight(transformPages(pages), transformDirectories(dirs));
                    childrenCacheRef.current.set(currentSpaceId, children);
                    setChildrenVersion((v) => v + 1);
                    setAllDirs(children.filter((c) => c.type === 'directory'));
                    setSpaces([]);
                })
                .catch(console.error)
                .finally(() => { if (!cancelled) setLoading(false); });
        } else {
            fetchJson(getApiBase(showArchived))
                .then((data) => {
                    if (cancelled) return;
                    const all = transformDirectories(data);
                    setAllDirs(all);
                    setSpaces(all.filter((d) => d.parentId === 0));
                })
                .catch(console.error)
                .finally(() => { if (!cancelled) setLoading(false); });
        }
        return () => { cancelled = true; };
    }, [showArchived, currentSpaceId, isSpaceMode]);

    /* ── Auto-expand to current path ──
       Walks UP from the target directory to the space root (or hub root),
       fetching directory metadata from the API when not available locally.
       This handles arbitrarily deep nesting. */
    useEffect(() => {
        const m = location.pathname.match(/^\/docs\/(space|page)\/(\d+)/);
        if (!m) return;

        if (isSpaceMode && !childrenCacheRef.current.has(currentSpaceId)) return;
        if (!isSpaceMode && allDirs.length === 0) return;

        let cancelled = false;
        const kind = m[1];
        const targetId = parseInt(m[2], 10);

        (async () => {
            let dirId;
            if (kind === 'page') {
                try {
                    const item = await fetchJson(`/decoupled-api/node/builder_page/${targetId}?display=decoupled`);
                    if (cancelled) return;
                    dirId = parseInt(item?.data?.directoryId, 10);
                    if (!Number.isFinite(dirId)) return;
                } catch { return; }
            } else {
                dirId = targetId;
            }

            const stopAtId = isSpaceMode ? currentSpaceId : 0;
            const chain = [];
            const localMap = new Map(allDirs.map((d) => [d.id, d]));
            let currentId = dirId;

            while (currentId && currentId !== stopAtId) {
                chain.unshift(currentId);

                let parentId = null;
                const local = localMap.get(currentId);
                if (local) {
                    parentId = local.parentId || 0;
                } else {
                    for (const children of childrenCacheRef.current.values()) {
                        const cached = children.find((c) => c.type === 'directory' && c.id === currentId);
                        if (cached) { parentId = cached.parentId || 0; break; }
                    }
                }

                if (parentId === null) {
                    if (cancelled) return;
                    try {
                        const res = await fetch(`/decoupled-api/taxonomy_term/doc_directory/${currentId}?display=decoupled`, { credentials: 'include' });
                        if (!res.ok || cancelled) return;
                        const json = await res.json();
                        const d = json?.data ?? json;
                        parentId = d?.parentId ? parseInt(d.parentId, 10) : 0;
                    } catch { return; }
                }

                currentId = parentId;
            }

            if (cancelled || chain.length === 0) return;
            setExpandedDirs((prev) => { const next = new Set(prev); chain.forEach((id) => next.add(id)); return next; });
            chain.forEach((id) => loadChildren(id));
        })();

        return () => { cancelled = true; };
    }, [location.pathname, allDirs, isSpaceMode, currentSpaceId, childrenVersion]);

    /* ── Cmd+K ── */
    useEffect(() => {
        const handler = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                effectiveSearchRef.current?.focus();
                effectiveSearchRef.current?.select?.();
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [effectiveSearchRef]);

    /* ── Load children ── */
    const loadChildren = useCallback(async (dirId) => {
        if (childrenCacheRef.current.has(dirId)) return;
        childrenCacheRef.current.set(dirId, []);
        setLoadingDirs((p) => new Set(p).add(dirId));
        try {
            const safeId = encodeURIComponent(dirId);
            const [dirs, pages] = await Promise.all([
                fetchJson(`${getApiBase(showArchivedRef.current)}&filter[0][f]=parent&filter[0][v]=${safeId}&sort[0][f]=weight&sort[0][d]=ASC`),
                fetchJson(`${getPagesBase(showArchivedRef.current)}&filter[0][f]=field_directory&filter[0][v]=${safeId}&sort[0][f]=field_weight&sort[0][d]=ASC`),
            ]);
            childrenCacheRef.current.set(dirId, mergeAndSortByWeight(transformPages(pages), transformDirectories(dirs)));
            setChildrenVersion((v) => v + 1);
        } catch (err) {
            console.error('Failed to load children:', err);
            childrenCacheRef.current.delete(dirId);
        } finally {
            setLoadingDirs((p) => { const next = new Set(p); next.delete(dirId); return next; });
        }
    }, []);

    const invalidateChildren = useCallback((dirId) => { childrenCacheRef.current.delete(dirId); loadChildren(dirId); }, [loadChildren]);

    const handleToggle = useCallback((dirId) => {
        setExpandedDirs((prev) => {
            const next = new Set(prev);
            if (next.has(dirId)) { next.delete(dirId); } else { next.add(dirId); loadChildren(dirId); }
            return next;
        });
    }, [loadChildren]);

    /* ── Context menu actions ── */
    const handleCtxAction = useCallback(async (action) => {
        const item = ctxMenu?.item;
        if (!item) return;
        setCtxMenu(null);
        const isDir = item.type === 'directory';

        switch (action) {
            case 'new-page':
                setCreateFor({ id: item.id, mode: 'blank' });
                break;
            case 'new-page-template':
                setCreateFor({ id: item.id, mode: 'template' });
                break;
            case 'new-dir':
                setCreateDirParent({ id: item.id, name: item.name || '' });
                break;
            case 'rename':
                setRenamingId(`${item.type}-${item.id}`);
                break;
            case 'move':
                setSettingsItem({ ...item, initialTab: 'move' });
                break;
            case 'archive':
                setPendingArchive(item);
                break;
            case 'unarchive':
                setPendingUnarchive(item);
                break;
            case 'delete':
                setPendingDelete(item);
                break;
        }
    }, [ctxMenu]);

    const handleRenameConfirm = useCallback(async (item, newName) => {
        setRenamingId(null);
        const isDir = item.type === 'directory';
        try {
            if (isDir) {
                await saveDocDirectory({ id: item.id, name: newName, parentId: item.parentId });
                if (isSpaceMode && item.parentId === currentSpaceId) {
                    invalidateChildren(currentSpaceId);
                } else if (item.parentId === 0) {
                    setSpaces((prev) => prev.map((s) => s.id === item.id ? { ...s, name: newName } : s));
                } else {
                    invalidateChildren(item.parentId);
                }
            } else {
                await saveDocPage({ id: item.id, title: newName });
                if (item.directoryId) invalidateChildren(item.directoryId);
            }
        } catch (err) { console.error('Rename failed:', err); }
    }, [invalidateChildren, isSpaceMode, currentSpaceId]);

    // Les écrans de création du builder NAVIGUENT vers la page créée sans
    // rappeler `onClose` : sur l'écran d'un espace, la navigation démonte la
    // pop-in, mais cette colonne-ci survit au changement de route — elle
    // resterait ouverte par-dessus la page neuve. On referme donc sur l'URL, et
    // on invalide au passage le répertoire visé pour qu'il montre sa page.
    useEffect(() => {
        if (!createFor) return;
        setCreateFor(null);
        invalidateChildren(createFor.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const handleDirCreated = useCallback(({ id: newId, parentId: pid }) => {
        setCreateDirParent(null);
        if (pid) invalidateChildren(pid);
        if (isSpaceMode) invalidateChildren(currentSpaceId);
        if (newId) navigate(`/docs/space/${newId}`);
    }, [invalidateChildren, isSpaceMode, currentSpaceId, navigate]);

    /* ── Delete confirm ── */
    const handleDeleteConfirm = useCallback(async () => {
        const item = pendingDelete;
        if (!item) return;
        const isDir = item.type === 'directory';
        const isRoot = isDir && !item.parentId;
        setDeleting(true);
        try {
            if (isDir) await deleteDocDirectory(item.id); else await deleteDocPage(item.id);
            setPendingDelete(null);
            if (isSpaceMode) {
                const parentRef = isDir ? item.parentId : item.directoryId;
                if (parentRef) invalidateChildren(parentRef);
            } else if (isDir && isRoot) {
                setSpaces((prev) => prev.filter((s) => s.id !== item.id));
            } else if (isDir && item.parentId) {
                invalidateChildren(item.parentId);
            } else if (!isDir && item.directoryId) {
                invalidateChildren(item.directoryId);
            }
            window.dispatchEvent(new CustomEvent('doc:entity-trashed', {
                detail: { type: isDir ? 'directory' : 'page', id: item.id, parentId: item.parentId || item.directoryId, isRoot },
            }));
            const currentHref = isDir ? `/docs/space/${item.id}` : `/docs/page/${item.id}`;
            if (location.pathname === currentHref) {
                const target = isDir && item.parentId ? `/docs/space/${item.parentId}` : '/docs';
                navigate(target);
            }
        } catch (err) { console.error('Trash failed:', err); }
        finally { setDeleting(false); }
    }, [pendingDelete, invalidateChildren, location.pathname, navigate, isSpaceMode]);

    /* ── Archive confirm ── */
    const handleArchiveConfirm = useCallback(async () => {
        const item = pendingArchive;
        if (!item) return;
        const isDir = item.type === 'directory';
        const isRoot = isDir && !item.parentId;
        setArchivingDoc(true);
        try {
            if (isDir) await archiveDocDirectory(item.id); else await archiveDocPage(item.id);
            setPendingArchive(null);
            if (isSpaceMode) {
                const parentRef = isDir ? item.parentId : item.directoryId;
                if (parentRef) invalidateChildren(parentRef);
            } else if (isDir && isRoot) {
                setSpaces((prev) => prev.filter((s) => s.id !== item.id));
            } else if (isDir && item.parentId) {
                invalidateChildren(item.parentId);
            } else if (!isDir && item.directoryId) {
                invalidateChildren(item.directoryId);
            }
            window.dispatchEvent(new CustomEvent('doc:entity-archived', {
                detail: { type: isDir ? 'directory' : 'page', id: item.id, parentId: item.parentId || item.directoryId, isRoot },
            }));
            const currentHref = isDir ? `/docs/space/${item.id}` : `/docs/page/${item.id}`;
            if (location.pathname === currentHref) {
                const target = isDir && item.parentId ? `/docs/space/${item.parentId}` : '/docs';
                navigate(target);
            }
        } catch (err) {
            console.error('Archive failed:', err);
            const msg = err.message?.includes('403')
                ? 'Permission refusée. Vous n\'avez pas les droits pour archiver cet élément.'
                : `Erreur : ${err.message}`;
            alert(msg);
        }
        finally { setArchivingDoc(false); }
    }, [pendingArchive, invalidateChildren, location.pathname, navigate, isSpaceMode]);

    /* ── Unarchive confirm ── */
    const handleUnarchiveConfirm = useCallback(async () => {
        const item = pendingUnarchive;
        if (!item) return;
        const isDir = item.type === 'directory';
        const isRoot = isDir && !item.parentId;
        setUnarchivingDoc(true);
        try {
            const entityType = isDir ? 'taxonomy_term' : 'node';
            const bundle = isDir ? 'doc_directory' : 'builder_page';
            await unarchiveEntity(entityType, bundle, item.id);
            setPendingUnarchive(null);
            if (isSpaceMode) {
                const parentRef = isDir ? item.parentId : item.directoryId;
                if (parentRef) invalidateChildren(parentRef);
            } else if (isDir && isRoot) {
                setSpaces((prev) => prev.filter((s) => s.id !== item.id));
            } else if (isDir && item.parentId) {
                invalidateChildren(item.parentId);
            } else if (!isDir && item.directoryId) {
                invalidateChildren(item.directoryId);
            }
        } catch (err) {
            console.error('Unarchive failed:', err);
            alert(`Erreur : ${err.message}`);
        } finally { setUnarchivingDoc(false); }
    }, [pendingUnarchive, invalidateChildren, isSpaceMode]);

    /* ── Move confirm (after modal validation) — reload affected branches ── */
    const handleMoveConfirm = useCallback(() => {
        const move = pendingMove;
        setPendingMove(null);
        if (!move) return;
        if (move.fromParentId) invalidateChildren(move.fromParentId);
        if (move.toParentId) invalidateChildren(move.toParentId);
    }, [pendingMove, invalidateChildren]);

    /* ── Toggle showArchived ── */
    const handleToggleShowArchived = useCallback(() => {
        setShowArchived(prev => {
            const next = !prev;
            try { localStorage.setItem(SHOW_ARCHIVED_KEY, next ? '1' : '0'); } catch { /* noop */ }
            return next;
        });
    }, []);

    /* ── Listen for external trash events (from DocSpaceActions / DocPageActions) ── */
    useEffect(() => {
        const handler = (e) => {
            const { type, id, parentId: pid, isRoot: root } = e.detail || {};
            if (isSpaceMode) {
                if (pid) invalidateChildren(pid);
            } else if (type === 'directory') {
                if (root) setSpaces((prev) => prev.filter((s) => s.id !== id));
                else if (pid) invalidateChildren(pid);
            } else if (type === 'page' && pid) {
                invalidateChildren(pid);
            }
        };
        window.addEventListener('doc:entity-trashed', handler);
        return () => window.removeEventListener('doc:entity-trashed', handler);
    }, [invalidateChildren, isSpaceMode]);

    /* ── Search filter ── */
    const filteredTree = useMemo(() => {
        if (!searchQuery.trim()) return null;
        const q = searchQuery.toLowerCase();
        const matchedDirs = allDirs.filter((d) => d.name.toLowerCase().includes(q));
        const matchedPages = [];
        for (const [, children] of childrenCacheRef.current) {
            for (const c of children) { if (c.type === 'page' && c.title.toLowerCase().includes(q)) matchedPages.push(c); }
        }
        const matchedChildDirs = [];
        if (isSpaceMode) {
            for (const [, children] of childrenCacheRef.current) {
                for (const c of children) { if (c.type === 'directory' && c.name.toLowerCase().includes(q) && !matchedDirs.some((d) => d.id === c.id)) matchedChildDirs.push(c); }
            }
        }
        return [
            ...matchedDirs.map((d) => ({ ...d, _match: true })),
            ...matchedChildDirs.map((d) => ({ ...d, _match: true })),
            ...matchedPages.map((p) => ({ ...p, _match: true })),
        ];
    }, [searchQuery, allDirs, childrenVersion, isSpaceMode]);

    /* ── Build tree items for dnd-kit-sortable-tree ── */
    const treeItems = useMemo(() => {
        const rootItems = isSpaceMode
            ? (childrenCacheRef.current.get(currentSpaceId) || [])
            : spaces;
        return buildTreeItems(childrenCacheRef.current, rootItems, expandedDirs, canDrag);
    }, [childrenVersion, expandedDirs, canDrag, isSpaceMode, currentSpaceId, spaces]);

    /* ── Context value for DnD tree items ── */
    const handleCtxMenuCb = useCallback((itm, cx, cy, anchor) => {
        setCtxMenu({ item: itm, x: cx, y: cy, anchor });
    }, []);
    const handleSettingsCb = useCallback((itm) => setSettingsItem(itm), []);
    const handleRenameCancelCb = useCallback(() => setRenamingId(null), []);

    const handleSettingsSaved = useCallback((savedItem) => {
        if (!savedItem) return;
        const isDir = savedItem.type === 'directory';
        if (isSpaceMode) {
            const parentRef = isDir ? savedItem.parentId : savedItem.directoryId;
            if (parentRef) invalidateChildren(parentRef);
            invalidateChildren(currentSpaceId);
        } else if (isDir && savedItem.parentId === 0) {
            setSpaces((prev) => prev.map((s) => s.id === savedItem.id ? { ...s, name: savedItem.name } : s));
        } else if (isDir && savedItem.parentId) {
            invalidateChildren(savedItem.parentId);
        } else if (!isDir && savedItem.directoryId) {
            invalidateChildren(savedItem.directoryId);
        }
        setSettingsItem(null);
    }, [invalidateChildren, isSpaceMode, currentSpaceId]);

    const dndTreeCtxValue = useMemo(() => ({
        expandedDirs,
        loadingDirs,
        handleToggle,
        activePath: location.pathname,
        onContextMenu: handleCtxMenuCb,
        onSettings: handleSettingsCb,
        navigate,
        renamingId,
        handleRenameConfirm,
        onRenameCancel: handleRenameCancelCb,
        isAdmin,
        canDrag,
    }), [expandedDirs, loadingDirs, handleToggle, location.pathname, handleCtxMenuCb, handleSettingsCb, navigate, renamingId, handleRenameConfirm, handleRenameCancelCb, isAdmin, canDrag]);

    /* ── Render tree (non-DnD fallback for hub mode) ── */
    const renderTree = (items, depth) => items.map((item) => {
        const isDir = item.type === 'directory';
        const isExpanded = expandedDirs.has(item.id);
        const children = childrenCacheRef.current.get(item.id) || [];
        const key = sortableId(item);
        return (
            <div key={key}>
                <TreeItemRow item={item} depth={depth} expandedDirs={expandedDirs} loadingDirs={loadingDirs} onToggle={handleToggle} activePath={location.pathname}
                    onContextMenu={(itm, cx, cy, anchor) => setCtxMenu({ item: itm, x: cx, y: cy, anchor })} onNavigate={navigate} onSettings={handleSettingsCb}
                    renaming={renamingId === key} onRenameConfirm={(newName) => handleRenameConfirm(item, newName)} onRenameCancel={() => setRenamingId(null)} isAdmin={isAdmin} />
                {isDir && isExpanded && (
                    <div>
                        {children.length > 0 ? renderTree(children, depth + 1) : (
                            !loadingDirs.has(item.id) && <p className="text-2xs text-stone-300 italic py-1.5" style={{ paddingLeft: `${(depth + 1) * 20 + 14}px` }}>Vide</p>
                        )}
                    </div>
                )}
            </div>
        );
    });

    const renderSearchResults = () => {
        if (!filteredTree) return null;
        if (filteredTree.length === 0) return <p className="text-xs text-stone-400 italic text-center py-6">Aucun résultat pour « {searchQuery} »</p>;
        return filteredTree.map((item) => {
            const isDir = item.type === 'directory';
            const href = isDir ? `/docs/space/${item.id}` : `/docs/page/${item.id}`;
            const isActive = location.pathname === href;
            return (
                <a key={`${item.type}-${item.id}`} href={href} onClick={(e) => { e.preventDefault(); navigate(href); }}
                    className={`flex items-center gap-2 py-1.5 px-3 rounded-lg text-xs transition-colors ${isActive ? 'bg-blue-50 text-blue-700 font-medium' : 'text-stone-600 hover:bg-stone-100'}`}>
                    {isDir ? <Folder size={14} className="text-stone-400 flex-shrink-0" /> : <FileText size={14} className="text-stone-400 flex-shrink-0" />}
                    <span className="truncate">{!isDir && item.icon ? <Picto name={item.icon} size={13} className="inline-block align-[-2px] mr-1" /> : null}{isDir ? item.name : item.title}</span>
                </a>
            );
        });
    };

    return (
        <>
            {/* Search */}
            <div className="px-3 pt-3 pb-1 flex-shrink-0">
                <div className={`flex items-center gap-2 w-full px-3 py-2 text-xs bg-white border rounded-lg transition-all ${searchFocused ? 'border-blue-400 ring-2 ring-blue-200/50' : 'border-stone-200 hover:border-stone-300'}`}>
                    <Search size={14} className="text-stone-400 flex-shrink-0" />
                    <input
                        ref={effectiveSearchRef}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => setSearchFocused(true)}
                        onBlur={() => setSearchFocused(false)}
                        onKeyDown={(e) => { if (e.key === 'Escape') { setSearchQuery(''); e.target.blur(); } }}
                        placeholder={isSpaceMode ? "Rechercher dans l'espace…" : "Rechercher…"}
                        className="flex-1 bg-transparent outline-none text-stone-700 placeholder:text-stone-400 text-xs"
                    />
                    {searchQuery ? (
                        <button type="button" onClick={() => setSearchQuery('')} className="text-stone-400 hover:text-stone-600" aria-label="Effacer la recherche"><X size={12} /></button>
                    ) : (
                        <kbd className="text-3xs font-mono bg-stone-100 px-1.5 py-0.5 rounded text-stone-400 border border-stone-200">⌘K</kbd>
                    )}
                </div>
            </div>

            {/* Tree or search results */}
            <div className="px-2 py-3">
                {searchQuery.trim() ? (
                    <div>
                        <span className="px-2.5 text-2xs font-semibold text-stone-400 uppercase tracking-widest mb-2 block">Résultats</span>
                        {renderSearchResults()}
                    </div>
                ) : isSpaceMode ? (
                    <>
                        {/* Le retour à la liste des espaces a rejoint l'EN-TÊTE de la
                            colonne (DocSidebarShell) : il y est toujours visible, là où
                            ce lien-ci défilait avec l'arborescence. Un seul lien vers
                            /docs dans la colonne — pas deux. */}
                        <div className="mb-2 flex items-center justify-between px-2.5">
                            <span className="text-2xs font-semibold text-stone-400 uppercase tracking-widest">Contenu</span>
                            <button
                                type="button"
                                onClick={handleToggleShowArchived}
                                aria-pressed={showArchived}
                                title={showArchived ? 'Masquer les archivés' : 'Afficher les archivés'}
                                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-all ${showArchived ? 'text-amber-700 bg-amber-100 hover:bg-amber-200 ring-1 ring-amber-200' : 'text-stone-400 hover:text-stone-600 hover:bg-stone-100'}`}
                            >
                                <Archive size={12} />
                                {showArchived ? 'Archivés ✓' : 'Archivés'}
                            </button>
                        </div>
                        {loading ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-5 w-5 border-2 border-stone-200 border-t-blue-500" />
                            </div>
                        ) : (
                            <>
                                {canDrag && treeItems.length > 0 ? (
                                    <DndTreeCtx.Provider value={dndTreeCtxValue}>
                                        <SortableTree
                                            items={treeItems}
                                            onItemsChanged={handleItemsChanged}
                                            TreeItemComponent={DndTreeItemComponent}
                                            indentationWidth={20}
                                            pointerSensorOptions={{ activationConstraint: { distance: 5 } }}
                                        />
                                    </DndTreeCtx.Provider>
                                ) : (
                                    renderTree(childrenCacheRef.current.get(currentSpaceId) || [], 0)
                                )}
                                {(childrenCacheRef.current.get(currentSpaceId) || []).length === 0 && !loadingDirs.has(currentSpaceId) && (
                                    <p className="text-xs text-stone-400 italic text-center py-6">Cet espace est vide.</p>
                                )}
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <div className="mb-2 flex items-center justify-between px-2.5">
                            <span className="text-2xs font-semibold text-stone-400 uppercase tracking-widest">Espaces</span>
                            <button
                                type="button"
                                onClick={handleToggleShowArchived}
                                aria-pressed={showArchived}
                                title={showArchived ? 'Masquer les archivés' : 'Afficher les archivés'}
                                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-all ${showArchived ? 'text-amber-700 bg-amber-100 hover:bg-amber-200 ring-1 ring-amber-200' : 'text-stone-400 hover:text-stone-600 hover:bg-stone-100'}`}
                            >
                                <Archive size={12} />
                                {showArchived ? 'Archivés ✓' : 'Archivés'}
                            </button>
                        </div>
                        {loading ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-5 w-5 border-2 border-stone-200 border-t-blue-500" />
                            </div>
                        ) : spaces.length === 0 ? (
                            <div className="px-2.5 text-center py-6">
                                <p className="text-xs text-stone-400 italic mb-3">Aucun espace documentaire.</p>
                                <button type="button" onClick={() => filtersCtx?.setShowSpaceForm?.(true)} className="text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors">+ Créer un espace</button>
                            </div>
                        ) : (
                            renderTree(spaces, 0)
                        )}
                    </>
                )}
            </div>

            {ctxMenu && (
                <ContextMenu x={ctxMenu.x} y={ctxMenu.y} anchor={ctxMenu.anchor} item={ctxMenu.item} onClose={() => setCtxMenu(null)} onAction={handleCtxAction} isAdmin={isAdmin} />
            )}

            {settingsItem && createPortal(
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" role="dialog" aria-modal="true">
                    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setSettingsItem(null)} />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl border border-stone-200 max-h-[85vh] flex flex-col">
                        <DirectorySettingsBody
                            itemId={settingsItem.id}
                            itemType={settingsItem.type}
                            currentName={settingsItem.type === 'directory' ? settingsItem.name : settingsItem.title}
                            currentIcon={settingsItem.icon || ''}
                            currentParentId={settingsItem.type === 'directory' ? settingsItem.parentId : settingsItem.directoryId}
                            initialTab={settingsItem.initialTab || 'rename'}
                            onClose={() => setSettingsItem(null)}
                            onSaved={() => handleSettingsSaved(settingsItem)}
                        />
                    </div>
                </div>,
                document.body,
            )}

            {/* Les deux écrans de création du builder, montés ici avec le
                répertoire visé : ce sont EUX que sert la barre d'un espace
                (`BuilderPageCreateMount`), et c'est `directoryId` qui range la
                page créée dans la bonne taxonomie. */}
            {createFor?.mode === 'blank' && createPortal(
                <BuilderCreateModal
                    onClose={() => setCreateFor(null)}
                    directoryId={createFor.id}
                />,
                document.body,
            )}

            {createFor?.mode === 'template' && createPortal(
                <BuilderTemplatePicker
                    onClose={() => setCreateFor(null)}
                    directoryId={createFor.id}
                />,
                document.body,
            )}

            {createDirParent && createPortal(
                <SpaceFormBody
                    isOpen
                    parentId={createDirParent.id}
                    parentName={createDirParent.name}
                    onClose={() => setCreateDirParent(null)}
                    onCreated={handleDirCreated}
                />,
                document.body,
            )}

            {pendingDelete && (() => {
                const isDir = pendingDelete.type === 'directory';
                const isRoot = isDir && !pendingDelete.parentId;
                const typeLabel = isDir ? (isRoot ? 'l\'espace' : 'le répertoire') : 'la page';
                const name = isDir ? pendingDelete.name : pendingDelete.title;
                return (
                    <ConfirmTrashModal
                        isOpen
                        onConfirm={handleDeleteConfirm}
                        onCancel={() => setPendingDelete(null)}
                        title={`Supprimer ${typeLabel} « ${name} » ?`}
                        description={isDir
                            ? `${isRoot ? 'Cet espace' : 'Ce répertoire'} et tout son contenu seront déplacés dans la corbeille. Vous pourrez les restaurer pendant 90 jours.`
                            : 'Cette page sera déplacée dans la corbeille. Vous pourrez la restaurer pendant 90 jours.'}
                        confirmLabel="Déplacer dans la corbeille"
                        loading={deleting}
                    />
                );
            })()}

            {pendingArchive && (() => {
                const isDir = pendingArchive.type === 'directory';
                const isRoot = isDir && !pendingArchive.parentId;
                const typeLabel = isDir ? (isRoot ? 'l\'espace' : 'le répertoire') : 'la page';
                const name = isDir ? pendingArchive.name : pendingArchive.title;
                return (
                    <ConfirmTrashModal
                        isOpen
                        onConfirm={handleArchiveConfirm}
                        onCancel={() => setPendingArchive(null)}
                        title={`Archiver ${typeLabel} « ${name} » ?`}
                        description={isDir
                            ? `${isRoot ? 'Cet espace' : 'Ce répertoire'} et tout son contenu seront archivés en lecture seule.`
                            : 'Cette page sera archivée en lecture seule.'}
                        confirmLabel="Archiver"
                        loading={archivingDoc}
                        variant="archive"
                    />
                );
            })()}

            {pendingUnarchive && (() => {
                const isDir = pendingUnarchive.type === 'directory';
                const isRoot = isDir && !pendingUnarchive.parentId;
                const typeLabel = isDir ? (isRoot ? 'l\'espace' : 'le répertoire') : 'la page';
                const name = isDir ? pendingUnarchive.name : pendingUnarchive.title;
                return (
                    <ConfirmTrashModal
                        isOpen
                        onConfirm={handleUnarchiveConfirm}
                        onCancel={() => setPendingUnarchive(null)}
                        title={`Désarchiver ${typeLabel} « ${name} » ?`}
                        description={isDir
                            ? `${isRoot ? 'Cet espace' : 'Ce répertoire'} et tout son contenu redeviendront actifs et modifiables.`
                            : 'Cette page redeviendra active et modifiable.'}
                        confirmLabel="Désarchiver"
                        loading={unarchivingDoc}
                    />
                );
            })()}

            <DocMovePreviewModal
                isOpen={!!pendingMove}
                moveData={pendingMove}
                onConfirm={handleMoveConfirm}
                onCancel={() => setPendingMove(null)}
            />
        </>
    );
}
